
import { useState } from 'react';

interface DemoBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DemoBookingModal({ isOpen, onClose }: DemoBookingModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    companySize: '',
    preferredDate: '',
    preferredTime: '',
    requirements: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = '请输入您的姓名';
    }

    if (!formData.company.trim()) {
      newErrors.company = '请输入公司名称';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = '请输入联系电话';
    } else if (!/^1[3-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = '请输入有效的手机号码';
    }

    if (!formData.requirements.trim()) {
      newErrors.requirements = '请简要描述您的需求';
    } else if (formData.requirements.length > 500) {
      newErrors.requirements = '需求描述不能超过500字';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const submitData = new URLSearchParams();
      submitData.append('name', formData.name);
      submitData.append('company', formData.company);
      submitData.append('phone', formData.phone);
      submitData.append('requirements', formData.requirements);

      const response = await fetch('https://readdy.ai/api/form/d5fr2fof0tcnoht871og', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          phone: '',
          email: '',
          companySize: '',
          preferredDate: '',
          preferredTime: '',
          requirements: ''
        });
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('提交失败:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-teal-600 text-white px-8 py-6 rounded-t-3xl z-10">
          <button
            onClick={onClose}
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-white hover:bg-white/20 rounded-full transition-all cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl backdrop-blur-sm">
              <i className="ri-play-circle-line text-3xl"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-1">预约产品演示</h3>
              <p className="text-blue-100 text-sm">我们的专家将为您详细演示系统功能</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8" data-readdy-form id="demo-booking-form">
          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-xl flex items-center gap-3">
              <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
              <div>
                <p className="text-green-800 font-semibold">提交成功！</p>
                <p className="text-green-600 text-sm">我们会尽快与您联系安排演示</p>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center gap-3">
              <i className="ri-error-warning-fill text-2xl text-red-600"></i>
              <div>
                <p className="text-red-800 font-semibold">提交失败</p>
                <p className="text-red-600 text-sm">请稍后重试或联系客服</p>
              </div>
            </div>
          )}

          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="demo-name" className="block text-sm font-bold text-gray-800 mb-2">
                  您的姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="demo-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3.5 border-2 ${errors.name ? 'border-red-300' : 'border-gray-200'} rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm hover:border-gray-300`}
                  placeholder="请输入您的姓名"
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="demo-phone" className="block text-sm font-bold text-gray-800 mb-2">
                  联系电话 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="demo-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-3.5 border-2 ${errors.phone ? 'border-red-300' : 'border-gray-200'} rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm hover:border-gray-300`}
                  placeholder="请输入联系电话"
                />
                {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="demo-company" className="block text-sm font-bold text-gray-800 mb-2">
                公司名称 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="demo-company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className={`w-full px-4 py-3.5 border-2 ${errors.company ? 'border-red-300' : 'border-gray-200'} rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm hover:border-gray-300`}
                placeholder="请输入公司名称"
              />
              {errors.company && <p className="mt-1 text-sm text-red-600">{errors.company}</p>}
            </div>

            <div>
              <label htmlFor="demo-requirements" className="block text-sm font-bold text-gray-800 mb-2">
                您的需求 <span className="text-red-500">*</span>
              </label>
              <textarea
                id="demo-requirements"
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
                rows={4}
                maxLength={500}
                className={`w-full px-4 py-3.5 border-2 ${errors.requirements ? 'border-red-300' : 'border-gray-200'} rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm hover:border-gray-300 resize-none`}
                placeholder="请简要描述您的需求和关注的功能点（限500字）"
              ></textarea>
              <div className="flex justify-between items-center mt-1">
                {errors.requirements && <p className="text-sm text-red-600">{errors.requirements}</p>}
                <p className="text-sm text-gray-500 ml-auto">{formData.requirements.length}/500</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mt-8 pt-6 border-t-2 border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl font-bold hover:from-blue-700 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  提交中...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-send-plane-fill"></i>
                  提交预约
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
